<template>
  <b-modal
    :title="isEdit ? 'ویرایش کد تخفیف' : 'ساخت کد تخفیف'"
    id="modal-coupon-add"
    size="lg"
    lazy
    no-close-on-backdrop
    no-close-on-esc
    :ok-title="isSubmitting ? 'در حال ثبت...' : isEdit ? 'ذخیره تغییرات' : 'ساخت کد'"
    :busy="isSubmitting"
    cancel-title="انصراف"
    @show="prefillForm"
    @cancel="closeModal"
    @close="closeModal"
    @ok.prevent="sendCouponCreateRequest"
  >
    <p class="coupon-intro">
      کاربر این کد را در سبد خرید وارد می‌کند تا تخفیف بگیرد.
    </p>

    <!-- ۱. کد برای چه چیزی است -->
    <section class="coupon-section">
      <div class="coupon-section__title">
        <span class="coupon-section__step">۱</span>
        <h6 class="mb-0">کد برای چه چیزی است؟</h6>
        <b-form-checkbox v-model="form.isForAllTargets" class="coupon-all-toggle">
          همه‌ی موارد
        </b-form-checkbox>
      </div>

      <p v-if="form.isForAllTargets" class="coupon-target coupon-target__all mt-0">
        <feather-icon icon="GlobeIcon" size="16" class="mr-50" />
        این کد روی همه‌ی پکیج‌ها، درس‌ها، استادهای خصوصی و پشتیبانی ساعتی اعمال می‌شود.
      </p>

      <template v-else>
        <option-cards :options="targetOptions" v-model="form.targetType" />

        <!-- پشتیبانی ساعتی مورد خاصی برای انتخاب ندارد -->
        <p v-if="form.targetType == COUPON_TARGET.supportLesson" class="coupon-target coupon-target__all">
          <feather-icon icon="PhoneIcon" size="16" class="mr-50" />
          روی همه‌ی خریدهای پکیج پشتیبانی ساعتی اعمال می‌شود.
        </p>

        <div v-else class="coupon-target">
          <div class="coupon-target__head">
            <span class="coupon-target__name">
              <feather-icon :icon="selectedTarget.icon" size="16" />
              کدام {{ selectedTarget.text }}؟
            </span>
            <option-cards
              compact
              class="coupon-target__scope"
              :options="scopeOptions"
              v-model="form.isForAllItems"
            />
          </div>

          <p v-if="form.isForAllItems" class="coupon-target__all">
            روی همه‌ی {{ selectedTarget.plural }} اعمال می‌شود.
          </p>
          <div v-else class="coupon-target__picker">
          <lesson-category-picker
            v-if="form.targetType == COUPON_TARGET.package"
            key="package-picker"
            isInDialog
            :defaultLessonCategoryId="form.targetValue"
            @getLessonCategory="(id) => (form.targetValue = id || null)"
          ></lesson-category-picker>
          <lesson-picker
            v-else-if="form.targetType == COUPON_TARGET.lesson"
            key="lesson-picker"
            :defaultLesson="selectedDefaults.lesson"
            @getLesson="(id) => (form.targetValue = id || null)"
          ></lesson-picker>
          <master-picker
            v-else
            key="master-picker"
            isInDialog
            :defaultSelectedMaster="selectedDefaults.master"
            @getMaster="(master) => (form.targetValue = master ? master.userId : null)"
          ></master-picker>
          </div>
        </div>
      </template>
    </section>

    <!-- ۲. مقدار تخفیف -->
    <section class="coupon-section">
      <h6 class="coupon-section__title">
        <span class="coupon-section__step">۲</span>
        مقدار تخفیف
      </h6>
      <option-cards :options="couponTypes" v-model="form.type" />
      <b-form-row class="mt-1">
        <b-col cols="12" :md="form.type == 1 ? 6 : 12">
          <b-form-group :label="valueLabel" label-for="coupon-value" class="mb-0">
            <b-input-group :append="form.type == 1 ? '٪' : 'تومان'">
              <b-form-input
                type="number"
                id="coupon-value"
                min="0"
                :formatter="formatValue"
                v-model="form.value"
                :placeholder="form.type == 1 ? 'مثلا ۲۰' : 'مثلا ۵۰۰۰۰۰'"
              ></b-form-input>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6" v-if="form.type == 1" class="mt-1 mt-md-0">
          <b-form-group label="سقف مبلغ تخفیف" label-for="coupon-max" class="mb-0">
            <b-input-group append="تومان">
              <b-form-input
                type="number"
                id="coupon-max"
                min="0"
                v-model="form.maxDiscountAmount"
                placeholder="خالی یعنی بدون سقف"
              ></b-form-input>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-form-row>
      <p v-if="valueSummary" class="coupon-summary">{{ valueSummary }}</p>
      <p class="coupon-note">
        <feather-icon icon="InfoIcon" size="14" class="mr-50" />
        با استفاده از این کد، تخفیف خود دوره/پکیج و تخفیف سطح وفاداری کاربر اعمال نمی‌شود
        و فقط همین کد حساب می‌شود.
      </p>
    </section>

    <!-- ۳. زمان اعتبار -->
    <section class="coupon-section">
      <h6 class="coupon-section__title">
        <span class="coupon-section__step">۳</span>
        زمان اعتبار
      </h6>
      <b-form-row>
        <b-col cols="12" md="6">
          <b-form-group label="از تاریخ" label-for="couponStart">
            <b-form-input
              class="couponStartDate"
              id="couponStart"
              :value="displayDate(form.startDate)"
              placeholder="تاریخ شروع"
              autocomplete="off"
            ></b-form-input>
            <date-picker
              v-model="form.startDate"
              class="absolute"
              customInput=".couponStartDate"
              display-format="jYYYY/jMM/jDD"
              input-format="jYYYY/jMM/jDD"
              format="YYYY-MM-DD"
            ></date-picker>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="تا تاریخ" label-for="couponEnd" class="mb-0">
            <b-form-input
              class="couponEndDate"
              id="couponEnd"
              :value="displayDate(form.endDate)"
              placeholder="تاریخ پایان"
              autocomplete="off"
            ></b-form-input>
            <date-picker
              v-model="form.endDate"
              class="absolute"
              customInput=".couponEndDate"
              display-format="jYYYY/jMM/jDD"
              input-format="jYYYY/jMM/jDD"
              format="YYYY-MM-DD"
            ></date-picker>
          </b-form-group>
        </b-col>
      </b-form-row>
    </section>

    <!-- ۴. استفاده‌کنندگان -->
    <section class="coupon-section">
      <h6 class="coupon-section__title">
        <span class="coupon-section__step">۴</span>
        چه کسی می‌تواند استفاده کند؟
      </h6>
      <option-cards :options="userScopes" v-model="form.isForSpecificUser" />

      <div v-if="form.isForSpecificUser" class="coupon-target__picker mt-1">
        <user-picker
          isInDialog
          :defaultSelectedUser="selectedDefaults.user"
          inputLabel="دانشجوی مورد نظر"
          @getUser="(id) => (form.userId = id || null)"
        ></user-picker>
      </div>

      <b-form-group label="تعداد دفعات قابل استفاده" label-for="coupon-quantity" class="mt-1 mb-0">
        <b-form-input
          type="number"
          id="coupon-quantity"
          min="0"
          v-model="form.maxUsageCount"
          placeholder="خالی یعنی بدون محدودیت"
        ></b-form-input>
      </b-form-group>

      <b-form-checkbox v-model="form.allowMultipleUsePerUser" class="mt-1">
        هر کاربر بتواند بیش از یک بار از این کد استفاده کند
      </b-form-checkbox>
      <small class="d-block text-muted mt-25">
        {{
          form.allowMultipleUsePerUser
            ? "یک کاربر می‌تواند چند بار از این کد استفاده کند."
            : "هر کاربر فقط یک بار می‌تواند از این کد استفاده کند."
        }}
      </small>
    </section>

    <!-- ۵. کد تخفیف -->
    <section class="coupon-section">
      <h6 class="coupon-section__title">
        <span class="coupon-section__step">۵</span>
        کد تخفیف
      </h6>
      <b-input-group>
        <b-form-input
          id="coupon-code"
          dir="ltr"
          class="coupon-code-input"
          autocomplete="off"
          v-model.trim="form.code"
          placeholder="مثلا FERMAN20"
        ></b-form-input>
        <b-input-group-append>
          <b-button variant="outline-primary" @click="generateCode">
            <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" />
            ساخت خودکار
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </section>
  </b-modal>
</template>

<script>
import LessonCategoryPicker from "../LessonCategories/LessonCategoryPicker.vue";
import MasterPicker from "../MasterActiveCourse/MasterPicker.vue";
import LessonPicker from "../Lessons/LessonPicker.vue";
import UserPicker from "../Users/UserPicker.vue";
import OptionCards from "./OptionCards.vue";
import { COUPON_TARGET } from "./couponTargets";
import { CreateCoupon, EditACoupon, GetAllCoupons } from "@/libs/Api/Coupon";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
  BModal,
  BCol,
  BFormRow,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";

// طبق بکند هر نوع فقط برای «یک» مورد خاص است؛ «همه» یعنی نوع ۱
const TARGETS = [
  { value: COUPON_TARGET.package, text: "پکیج", plural: "پکیج‌ها", icon: "PackageIcon", description: "پکیج‌های آموزشی" },
  { value: COUPON_TARGET.lesson, text: "درس", plural: "درس‌ها", icon: "LayersIcon", description: "درس‌ها و کلاس‌ها" },
  { value: COUPON_TARGET.master, text: "استاد خصوصی", plural: "استادهای خصوصی", icon: "UserIcon", description: "جلسات خصوصی" },
  {
    value: COUPON_TARGET.supportLesson,
    text: "پشتیبانی ساعتی",
    plural: "پکیج‌های پشتیبانی ساعتی",
    icon: "PhoneIcon",
    description: "پکیج پشتیبانی تلفنی",
  },
];

function createEmptyForm() {
  return {
    // پیش‌فرض: همه‌ی موارد (نوع ۱)
    isForAllTargets: true,
    targetType: COUPON_TARGET.package,
    // پیش‌فرض: همه‌ی آیتم‌های نوع انتخاب‌شده
    isForAllItems: true,
    targetValue: null,
    // Types: { 1: درصدی, 2: مبلغ ثابت }
    type: 1,
    value: null,
    maxDiscountAmount: null,
    startDate: new Date(Date.now()),
    endDate: null,
    isForSpecificUser: false,
    userId: null,
    maxUsageCount: null,
    // پیش‌فرض: هر کاربر فقط یک بار
    allowMultipleUsePerUser: false,
    code: null,
  };
}

export default {
  emits: ["reFetch"],
  props: {
    // اگر مقدار داشته باشد، فرم در حالت ویرایش همین کد باز می‌شود
    editCoupon: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      COUPON_TARGET,
      targetOptions: TARGETS,
      scopeOptions: [
        { text: "همه", value: true },
        { text: "مورد خاص", value: false },
      ],
      couponTypes: [
        { text: "درصدی", value: 1, icon: "PercentIcon", description: "مثلا ۲۰٪ از مبلغ" },
        { text: "مبلغ ثابت", value: 2, icon: "DollarSignIcon", description: "مثلا ۵۰۰ هزار تومان" },
      ],
      userScopes: [
        { text: "همه‌ی دانشجویان", value: false, icon: "UsersIcon", description: "هر کسی کد را داشته باشد" },
        { text: "یک دانشجوی خاص", value: true, icon: "UserCheckIcon", description: "فقط فرد انتخاب‌شده" },
      ],
      form: createEmptyForm(),
      // هنگام پر کردن فرم از کد موجود، واکنش watcherها نباید انتخاب‌ها را پاک کند
      isPrefilling: false,
      // جلوگیری از ارسال دوباره با کلیک‌های پشت سر هم
      isSubmitting: false,
      // در ویرایش، مورد انتخاب‌شده‌ی قبلی تا داخل pickerها نمایش داده شود
      selectedDefaults: { user: null, master: null, lesson: null },
    };
  },
  watch: {
    // با عوض شدن نوع یا محدوده، انتخاب قبلی معتبر نیست
    // با عوض شدن نوع، انتخاب قبلی معتبر نیست
    "form.targetType"() {
      if (this.isPrefilling) return;
      this.form.isForAllItems = true;
      this.clearTargetSelection();
    },
    "form.isForAllTargets"() {
      if (this.isPrefilling) return;
      this.form.isForAllItems = true;
      this.clearTargetSelection();
    },
    "form.isForAllItems"() {
      if (this.isPrefilling) return;
      this.clearTargetSelection();
    },
    "form.isForSpecificUser"(value) {
      if (this.isPrefilling) return;
      if (!value) {
        this.form.userId = null;
        this.selectedDefaults.user = null;
      }
    },
  },
  methods: {
    clearTargetSelection() {
      this.form.targetValue = null;
      this.selectedDefaults.master = null;
      this.selectedDefaults.lesson = null;
    },
    // اسم کاربر و مورد هدف (userName/targetName) در خود خروجی کد تخفیف می‌آید
    setSelectedDefaults(coupon) {
      const { userId, userName, targetType, targetValue, targetName } = coupon;
      this.selectedDefaults = {
        user: userId ? { userId, fullName: userName || `#${userId}` } : null,
        master:
          targetValue && targetType == COUPON_TARGET.master
            ? { userId: targetValue, fullName: targetName || `#${targetValue}` }
            : null,
        lesson:
          targetValue && targetType == COUPON_TARGET.lesson
            ? { lessonId: targetValue, title: targetName || `#${targetValue}` }
            : null,
      };
    },
    prefillForm() {
      const coupon = this.editCoupon;
      this.selectedDefaults = { user: null, master: null, lesson: null };
      if (!coupon) {
        this.form = createEmptyForm();
        return;
      }
      this.isPrefilling = true;
      this.setSelectedDefaults(coupon);
      const isAll = coupon.targetType == COUPON_TARGET.all;
      this.form = {
        ...createEmptyForm(),
        isForAllTargets: isAll,
        targetType: isAll ? COUPON_TARGET.package : coupon.targetType,
        isForAllItems: !isAll && !coupon.targetValue,
        targetValue: coupon.targetValue || null,
        type: coupon.type,
        value: coupon.value,
        maxDiscountAmount: coupon.maxDiscountAmount || null,
        // انتخابگر تاریخ مقدار را با format="YYYY-MM-DD" می‌خواند؛ تاریخ کامل بکند (با ساعت) را
        // نمی‌شناخت و همان را میلادی داخل فیلد می‌نوشت
        startDate: this.toPickerDate(coupon.startDate),
        endDate: this.toPickerDate(coupon.endDate),
        isForSpecificUser: Boolean(coupon.userId),
        userId: coupon.userId || null,
        maxUsageCount: coupon.maxUsageCount || null,
        allowMultipleUsePerUser: Boolean(coupon.allowMultipleUsePerUser),
        code: coupon.code,
      };
      // watcherها پس از همین چرخه اجرا می‌شوند؛ بعد از آن دوباره فعال شوند
      this.$nextTick(() => {
        this.isPrefilling = false;
      });
    },
    toPickerDate(date) {
      return date ? String(date).slice(0, 10) : null;
    },
    displayDate(date) {
      if (!date) return "";
      const parsed = new Date(date);
      return isNaN(parsed) ? "" : parsed.toLocaleDateString("fa-IR-u-nu-latn");
    },
    formatValue(value) {
      if (this.form.type == 1 && Number(value) > 100) return 100;
      return value;
    },
    generateCode() {
      const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
      let code = "FM";
      for (let i = 0; i < 6; i++) {
        code += chars[Math.floor(Math.random() * chars.length)];
      }
      this.form.code = code;
    },
    backendMessage(response) {
      const errors = response && response.errors;
      if (typeof errors === "string") return errors;
      if (Array.isArray(errors)) return errors.join("، ");
      if (errors && typeof errors === "object") {
        const first = Object.values(errors).flat()[0];
        return first ? String(first) : "";
      }
      return (response && response.message) || "";
    },
    showWarning(title) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: "AlertTriangleIcon", variant: "warning" },
      });
    },
    validate() {
      const form = this.form;
      const hasPicker = form.targetType != COUPON_TARGET.supportLesson;
      if (!form.isForAllTargets && hasPicker && !form.isForAllItems && !form.targetValue) {
        return `یک ${this.selectedTarget.text} انتخاب کنید یا «همه» را بزنید`;
      }
      if (!form.value) return "مقدار تخفیف را وارد کنید";
      if (!form.endDate) return "تاریخ پایان را وارد کنید";
      if (new Date(form.endDate) < new Date(form.startDate)) {
        return "تاریخ پایان باید بعد از تاریخ شروع باشد";
      }
      if (form.isForSpecificUser && !form.userId) return "دانشجوی مورد نظر را انتخاب کنید";
      if (!form.code) return "کد تخفیف را وارد کنید";
      return null;
    },
    // مدل Coupon در بکند
    buildPayload() {
      const form = this.form;
      return {
        isDeleted: false,
        couponId: 0,
        type: form.type,
        value: Number(form.value),
        maxDiscountAmount:
          form.type == 1 && form.maxDiscountAmount ? Number(form.maxDiscountAmount) : null,
        startDate: new Date(form.startDate),
        endDate: new Date(form.endDate),
        code: form.code,
        targetType: form.isForAllTargets ? COUPON_TARGET.all : form.targetType,
        targetValue:
          form.isForAllTargets || form.isForAllItems || form.targetType == COUPON_TARGET.supportLesson
            ? null
            : form.targetValue,
        userId: form.isForSpecificUser ? form.userId : null,
        maxUsageCount: form.maxUsageCount ? Number(form.maxUsageCount) : null,
        allowMultipleUsePerUser: Boolean(form.allowMultipleUsePerUser),
        isActive: true,
        // تاریخ ساخت را بکند خودش ثبت می‌کند
        ...(this.isEdit
          ? {
              couponId: this.editCoupon.couponId,
              isActive: this.editCoupon.isActive,
              createDate: this.editCoupon.createDate,
            }
          : {}),
      };
    },
    // آیا کد دیگری با همین متن وجود دارد؟ (فیلتر Code بکند بخشی و بدون حساسیت به حروف است)
    findDuplicateCode(code) {
      return new Promise((resolve) => {
        let request = new GetAllCoupons(this);
        request.setRequestParam({ pageNumber: 1, count: 50, code });
        request.get(
          (response) => {
            const list = (response && response.data && response.data.coupons) || [];
            const ownId = this.isEdit ? this.editCoupon.couponId : null;
            resolve(
              list.find(
                (item) =>
                  String(item.code).toLowerCase() == String(code).toLowerCase() &&
                  item.couponId != ownId
              ) || null
            );
          },
          () => resolve(null)
        );
      });
    },
    async sendCouponCreateRequest() {
      if (this.isSubmitting) return;
      const error = this.validate();
      if (error) {
        this.showWarning(error);
        return;
      }
      this.isSubmitting = true;
      try {
        let _this = this;
        const duplicate = await _this.findDuplicateCode(_this.form.code);
        if (duplicate) {
          _this.showWarning(`کد «${duplicate.code}» قبلاً ساخته شده است؛ کد دیگری انتخاب کنید`);
          return;
        }
        let request = _this.isEdit ? new EditACoupon(_this) : new CreateCoupon(_this);
        request.setRequestParamDataObj(_this.buildPayload());
        await request.fetch((response) => {
          if (response && response.isSuccess === true) {
            _this.$toast({
              component: ToastificationContent,
              props: {
                title: _this.isEdit ? "کد تخفیف ویرایش شد" : "کد تخفیف ساخته شد",
                icon: "CheckIcon",
                variant: "success",
              },
            });
            _this.$emit("reFetch");
            _this.closeModal();
          } else {
            _this.showWarning(
              _this.backendMessage(response) ||
                (_this.isEdit
                  ? "ویرایش کد تخفیف انجام نشد"
                  : "ساخت کد تخفیف انجام نشد؛ ممکن است این کد تکراری باشد")
            );
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isSubmitting = false;
      }
    },
    closeModal() {
      this.form = createEmptyForm();
      this.$bvModal.hide("modal-coupon-add");
    },
  },
  computed: {
    isEdit() {
      return Boolean(this.editCoupon);
    },
    selectedTarget() {
      return TARGETS.find((target) => target.value == this.form.targetType) || TARGETS[0];
    },
    valueLabel() {
      return this.form.type == 2 ? "مبلغ تخفیف" : "درصد تخفیف";
    },
    valueSummary() {
      const value = Number(this.form.value);
      if (!value) return "";
      const format = (n) => Intl.NumberFormat("fa-IR").format(n);
      if (this.form.type == 2) return `${format(value)} تومان تخفیف ثابت`;
      const max = Number(this.form.maxDiscountAmount);
      return max
        ? `${format(value)}٪ تخفیف، حداکثر ${format(max)} تومان`
        : `${format(value)}٪ تخفیف، بدون سقف`;
    },
  },
  components: {
    OptionCards,
    UserPicker,
    LessonCategoryPicker,
    MasterPicker,
    LessonPicker,
    BModal,
    BCol,
    BFormRow,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BFormCheckbox,
  },
};
</script>

<style scoped>
.coupon-intro {
  margin: -0.25rem 0 1rem;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.coupon-section + .coupon-section {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}

.coupon-section__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.coupon-section__title h6 {
  font-weight: 600;
}

.coupon-section__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  font-size: 0.8rem;
}

/* تیک «همه‌ی موارد» به انتهای ردیف عنوان می‌رود (در RTL سمت چپ) */
.coupon-all-toggle {
  margin-inline-start: auto;
  font-weight: 500;
}

.coupon-note {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: 0.6rem 0 0;
  font-size: 0.8rem;
  color: #6e6b7b;
}

.coupon-target {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: #f8f8f8;
}

.coupon-target__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.5rem;
}

.coupon-target__name {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
}

.coupon-target__scope {
  flex: 0 1 260px;
}

.coupon-target__picker {
  margin-top: 0.75rem;
}

.coupon-target__all {
  margin: 0.6rem 0 0;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.coupon-summary {
  margin: 0.6rem 0 0;
  padding: 0.5rem 0.75rem;
  border-radius: 0.4rem;
  background-color: rgba(40, 199, 111, 0.1);
  color: #28c76f;
  font-size: 0.85rem;
  font-weight: 600;
}

.coupon-code-input {
  font-family: monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dark-layout .coupon-section + .coupon-section {
  border-top-color: #3b4253;
}

.dark-layout .coupon-target {
  background-color: #161d31;
}

@media (max-width: 575.98px) {
  .coupon-target__scope {
    flex-basis: 100%;
  }
}
</style>
